import React, { useState } from "react";
import axios from "axios";
import { X, Repeat, CalendarClock, Loader2 } from "lucide-react";
import { API } from "../config";

const FREQUENCIES = [
  { weeks: 4, label: "Every 4 weeks", sub: "Best for fast-movers" },
  { weeks: 6, label: "Every 6 weeks", sub: "Most popular" },
  { weeks: 8, label: "Every 8 weeks", sub: "Steady supply" },
  { weeks: 12, label: "Every 12 weeks", sub: "Quarterly top-up" },
];

/**
 * Modal for setting up an Auto-Reorder reminder on a product.
 * Props:
 *   product:    { id, name, image_url, price, stock }
 *   defaultQty: number (typical qty from history, defaults to 1)
 *   onClose():  required
 *   onSuccess(subscription): optional callback
 */
export default function AutoReorderModal({ product, defaultQty = 1, onClose, onSuccess }) {
  const [qty, setQty] = useState(Math.max(1, defaultQty || 1));
  const [weeks, setWeeks] = useState(6);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const handleSave = async () => {
    setError("");
    setSaving(true);
    try {
      const res = await axios.post(`${API}/subscriptions`, {
        product_id: product.id,
        quantity: qty,
        frequency_weeks: weeks,
      });
      setDone(true);
      onSuccess && onSuccess(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not set up Auto-Reorder. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="auto-reorder-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gradient-to-br from-[#003544] to-[#00a99d] text-white px-6 py-5 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <Repeat className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-lg font-bold">Set up Auto-Reorder</h3>
              <p className="text-xs text-white/80">Smart reminders. Never run out.</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="auto-reorder-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {done ? (
          <div className="p-6 text-center" data-testid="auto-reorder-success">
            <CalendarClock className="w-12 h-12 text-[#00a99d] mx-auto mb-3" />
            <h4 className="text-lg font-bold text-gray-900 mb-1">Auto-Reorder set up!</h4>
            <p className="text-sm text-gray-600 mb-5">
              We'll email you in {weeks} weeks with a one-click checkout link.
              You can pause or cancel any time from <strong>My Account → Auto-Reorders</strong>.
            </p>
            <button
              onClick={onClose}
              className="bg-[#00a99d] hover:bg-[#008c82] text-white font-semibold px-5 py-2.5 rounded-lg"
              data-testid="auto-reorder-done"
            >
              Done
            </button>
          </div>
        ) : (
          <div className="p-6">
            <div className="flex items-center gap-3 mb-5 p-3 bg-gray-50 rounded-lg">
              <img
                src={product.image_url || "https://via.placeholder.com/60"}
                alt=""
                className="w-12 h-12 object-contain rounded bg-white"
              />
              <div className="min-w-0">
                <p className="font-semibold text-sm text-gray-900 line-clamp-2">{product.name}</p>
                <p className="text-xs text-gray-500">£{product.price.toFixed(2)} each</p>
              </div>
            </div>

            <div className="mb-5">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Quantity per reminder</label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setQty(Math.max(1, qty - 1))}
                  className="w-9 h-9 border rounded flex items-center justify-center hover:bg-gray-50"
                >
                  −
                </button>
                <span className="w-12 text-center font-semibold" data-testid="auto-reorder-qty">{qty}</span>
                <button
                  type="button"
                  onClick={() => setQty(qty + 1)}
                  className="w-9 h-9 border rounded flex items-center justify-center hover:bg-gray-50"
                >
                  +
                </button>
              </div>
            </div>

            <div className="mb-5">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Reminder frequency</label>
              <div className="grid grid-cols-2 gap-2">
                {FREQUENCIES.map((f) => (
                  <button
                    key={f.weeks}
                    type="button"
                    onClick={() => setWeeks(f.weeks)}
                    className={`text-left p-3 rounded-lg border-2 transition ${
                      weeks === f.weeks
                        ? "border-[#00a99d] bg-[#00a99d]/5 ring-2 ring-[#00a99d]/20"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                    data-testid={`auto-reorder-freq-${f.weeks}`}
                  >
                    <div className="font-semibold text-sm text-gray-900">{f.label}</div>
                    <div className="text-xs text-gray-500">{f.sub}</div>
                  </button>
                ))}
              </div>
            </div>

            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-md p-3" data-testid="auto-reorder-error">
                {error}
              </div>
            )}

            <p className="text-xs text-gray-500 mb-4">
              We'll send you a reminder email at the chosen frequency with a one-click checkout link.
              No card is charged automatically.
            </p>

            <button
              onClick={handleSave}
              disabled={saving}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#00a99d] hover:bg-[#008c82] disabled:bg-gray-400 text-white font-semibold py-3 rounded-lg transition"
              data-testid="auto-reorder-save"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Repeat className="w-4 h-4" />}
              {saving ? "Setting up…" : "Activate Auto-Reorder"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
